import { flushSync } from 'react-dom';

/** Whether the page may animate: not where the user asks for less motion, or can't say. */
export function motionAllowed(): boolean {
  return typeof window.matchMedia === 'undefined'
    ? false
    : window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
}

let latest: ViewTransition | null = null;
let latestKind = '';
// While a transition makes its change, any change that change sets off goes along with it.
let applying = false;

interface TransitionOptions {
  /** Runs once the change is on the page. */
  readonly after?: () => void;
  /** Another change of the same kind meanwhile, as while Undo is held down, joins this one. */
  readonly join?: boolean;
}

/**
 * Makes a change to the page as a view transition, where the browser has them: what moves or
 * resizes glides into place, and the rest cross-fades (see layout.css). While it runs, the root's
 * data-transition is `kind`, so the styles can tell one change from another. From the moment the
 * change is made, data-transition-phase is `new` as well, so they can name what only leaves.
 */
export function withViewTransition(
  kind: string,
  change: () => void,
  { after, join = false }: TransitionOptions = {},
): void {
  if (
    applying ||
    (join && latest && latestKind === kind) ||
    !('startViewTransition' in document) ||
    !motionAllowed()
  ) {
    change();
    after?.();
    return;
  }
  const root = document.documentElement;
  root.dataset.transition = kind;
  delete root.dataset.transitionPhase;
  const transition = document.startViewTransition(() => {
    applying = true;
    root.dataset.transitionPhase = 'new';
    try {
      flushSync(change);
    } finally {
      applying = false;
    }
  });
  latest = transition;
  latestKind = kind;
  if (after) transition.updateCallbackDone.then(after, after);
  // A newer transition takes over the attribute; only the last one clears it.
  const done = () => {
    if (latest !== transition) return;
    latest = null;
    latestKind = '';
    delete root.dataset.transition;
    delete root.dataset.transitionPhase;
  };
  transition.finished.then(done, done);
}
